import React, { useState, useEffect, useMemo } from "react";
import { TextField, MenuItem, Grid, Typography, Button, Collapse, Chip, CircularProgress, Checkbox, ListItemText } from "@mui/material";
import { FilterList as FilterListIcon } from "@mui/icons-material";
import axios from "axios";
import NonConformanceTable from './NonConformanceTable';

// Form mappings: UI display name to API name
const formMappings = {
  "Pre-Lamination": "PreLamination",
  "Post-Lamination": "PostLamination",
  "Bill of material verification": "BOM",
  "Stringer": "Stringer",
  "Lamination machine parameters verification": "LaminationMachine",
  "Calibration data verification of secondary module": "calibrationVerificationOfSecondaryModule",
  "Primary (Golden) Module Calibration record": "PrimaryModuleCalibrationRecord",
  "Frame sealant weight verification": "FrameSealantWeightVerification",
  "Junction Box sealant weight verification": "JunctionBoxSealantWeightVerification",
  "Junction Box Potting material ratio verification": "JunctionBoxPottingMaterial",
  "Module dimensions check": "ModuleDimensionCheck",
};

// Mapping of form names to their subform mappings
const subFormMappingsByForm = {
  "Pre-Lamination": {
    "Shop Floor": "shopFloor_pre",
    "Cell and Cutting Machine": "CellAndCuttingMachine_pre",
    "Encapsulation Material (Front)": "encapsulationMaterialFront_pre",
    "Encapsulation Material (Back)": "encapsulationMaterialBack_pre",
    "Glass (Front & Back)": "GlassFrontBack_pre",
    "Tabbing & Stringing": "TabbingStringing_pre",
    "Auto Bussing": "autoBussing_pre",
    "Barcode/RFID": "barCodeRFID_pre",
    "Pre-Lamination EL & Re-work": "PreLaminationELRework_pre",
  },
  "Post-Lamination": {
    "Lamination": "Lamination_post",
    "Trimming/VI at 90 Degree": "Trimming_post",
    "Framing": "Framing_post",
    "JB Assembling & Soldering": "JbAssemblingSoldering_post",
    "Curing": "Curing_post",
    "Cleaning": "Cleaning_post",
    "Electrical Test": "electricalTest_post",
    "HiPot": "HiPot_post",
    "Post-Lamination EL": "PostLaminationEL_post",
    "Visual Inspection": "VisualInspections_post",
    "Packing": "Packing_post",
  },
  "Bill of material verification": {
    "Adhesive Sealant": "AdhesiveSealant",
    "Cell": "Cell",
    "Frame": "Frame",
    "Cell Fixing Type": "cellFixingType",
    "EVA Back": "evaBack",
    "EVA Front": "evaFront",
    "Flux": "flux",
    "Glass Back": "glassBack",
    "Glass Front": "glassFront",
    "Interconnector": "interconnector",
    "Junction Box": "junctionBox",
    "Potting Material": "pottingMaterial",
    "String Connector": "stringConnector",
  },
};

const NonConformanceReport = () => {
  const [supplier, setSupplier] = useState("");
  const [shift, setShift] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [formName, setFormName] = useState([]);
  const [subForm, setSubForm] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierShifts, setSupplierShifts] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [availableSubForms, setAvailableSubForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  // Form names for UI display
  const formNames = Object.keys(formMappings);

  // Inverse form mappings: API to UI
  const inverseFormMappings = Object.fromEntries(
    Object.entries(formMappings).map(([ui, api]) => [api, ui])
  );

  // Combine all subform mappings for inverse lookup
  const allSubFormMappings = Object.values(subFormMappingsByForm).reduce(
    (acc, curr) => ({ ...acc, ...curr }),
    {}
  );

  const inverseSubFormMappings = Object.fromEntries(
    Object.entries(allSubFormMappings).map(([ui, api]) => [api, ui])
  );

  // Fetch suppliers when the component mounts
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const selectedProject = localStorage.getItem("selectedProject");
        if (!selectedProject) {
          setError("Project ID not found in local storage");
          return;
        }

        const response = await axios.post(`${baseURL}get_supplier_names`, {
          projectId: selectedProject,
        });

        if (!Array.isArray(response.data)) {
          setError("Invalid response format from supplier API");
          return;
        }

        const supplierData = response.data
          .map((info) => {
            if (!info.name) {
              console.warn("Skipping invalid supplier entry:", info);
              return null;
            }
            return {
              name: info.name,
              shift: info.shift,
            };
          })
          .filter(Boolean);

        setSuppliers(supplierData);
      } catch (error) {
        setError("Error collecting supplier information");
        console.error("Supplier fetch error:", error);
      }
    };
    fetchSuppliers();
  }, []);

  // Handle supplier change
  const handleSupplierChange = (e) => {
    const selectedSupplierName = e.target.value;
    setSupplier(selectedSupplierName);
    setShift([]);

    const selectedSupplierData = suppliers.find((sup) => sup.name === selectedSupplierName);
    if (selectedSupplierData) {
      const shiftOptions =
        selectedSupplierData.shift === "MorningAfternoonNight"
          ? ["Morning", "Afternoon", "Night"]
          : ["Day", "Night"];
      setSupplierShifts(shiftOptions);
    } else {
      setSupplierShifts([]);
    }
  };

  // Handle form name change
  const handleFormNameChange = (e) => {
    let selectedForms = e.target.value;

    const lastClicked = selectedForms[selectedForms.length - 1];

    if (lastClicked === "all") {
      if (formName.length === formNames.length) {
        selectedForms = []; // Deselect all
      } else {
        selectedForms = [...formNames]; // Select all
      }
    }

    // Remove "all" from the final selection
    selectedForms = selectedForms.filter(item => item !== "all");

    setFormName(selectedForms);
    setSubForm([]); // Reset subForm when formName changes
  };

  const handleSubFormChange = (e) => {
    let selectedSubForms = e.target.value;

    const lastClicked = selectedSubForms[selectedSubForms.length - 1];

    if (lastClicked === "all") {
      if (subForm.length === availableSubForms.length) {
        selectedSubForms = []; // Deselect all
      } else {
        selectedSubForms = [...availableSubForms]; // Select all
      }
    }

    // Remove "all" from the final selection
    selectedSubForms = selectedSubForms.filter(item => item !== "all");

    setSubForm(selectedSubForms);
  };

// Optional: Keep your existing useEffect to clean invalid subforms
useEffect(() => {
  setSubForm((prev) => prev.filter((sub) => availableSubForms.includes(sub)));
}, [availableSubForms]);

  // Filter subForm when availableSubForms changes
  useEffect(() => {
    setSubForm((prev) => prev.filter((sub) => availableSubForms.includes(sub)));
  }, [availableSubForms]);

  // Fetch data when dependencies change (excluding subForm), with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      const fetchData = async () => {
        const projectId = localStorage.getItem("selectedProject");
        if (!projectId || !supplier || shift.length === 0 || !startDate || !endDate || formName.length === 0) {
          setTableData([]);
          setAvailableSubForms([]);
          return;
        }

        setLoading(true);
        setError(null);

        try {
          const apiFormNames = formName.map((name) => formMappings[name]);

          const response = await axios.get(`${baseURL}ncr`, {
            params: {
              projectId,
              inspectionSupplier: supplier,
              inspectionShift: shift.join(","),
              date: startDate,
              endDate: endDate,
              formName: apiFormNames.join(","),
            },
          });

          if (response.data.status === "success" && Array.isArray(response.data.data)) {
            // Ensure remark field is present, default to empty string if missing
            const processedData = response.data.data.map((item) => ({
              ...item,
              remark: item.remark || '',
            }));
            setTableData(processedData);

            // Extract unique subforms from data
            const uniqueApiSubForms = [...new Set(processedData.map((item) => item.subform || '').filter(Boolean))];

            // Get allowed subforms in the original mapped order
            const orderedUiSubForms = [];
            const seen = new Set();

            for (const form of formName) {
              const subFormMap = subFormMappingsByForm[form] || {};
              for (const [uiName, apiName] of Object.entries(subFormMap)) {
                if (uniqueApiSubForms.includes(apiName) && !seen.has(uiName)) {
                  seen.add(uiName);
                  orderedUiSubForms.push(uiName);
                }
              }
            }

            setAvailableSubForms(orderedUiSubForms); // Preserves original mapping order
          } else {
            setError(response.data.message || "Failed to fetch data");
            setTableData([]);
            setAvailableSubForms([]);
          }
        } catch (error) {
          setError("Failed to fetch data");
          console.error("Fetch error:", error);
          setTableData([]);
          setAvailableSubForms([]);
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }, 500); // Debounce by 500ms

    return () => clearTimeout(timer);
  }, [endDate, supplier, shift, startDate, formName]);

  // Compute filtered data based on selected subForms (client-side)
  const filteredData = useMemo(() => {
    if (subForm.length === 0) return tableData;

    const selectedApiSubForms = subForm.map((ui) => allSubFormMappings[ui]).filter(Boolean);
    return tableData.filter((item) => selectedApiSubForms.includes(item.subform));
  }, [tableData, subForm, allSubFormMappings]);

  // Toggle filter visibility
  const toggleFilters = () => {
    setShowFilters((prev) => !prev);
  };

  return (
    <div style={{ padding: "20px" }}>
      <Typography variant="h5" gutterBottom>
        Non-Conformance Report
      </Typography>

      <Button
        variant="outlined"
        startIcon={<FilterListIcon />}
        onClick={toggleFilters}
        style={{ marginBottom: "20px" }}
      >
        {showFilters ? "Hide Filters" : "Show Filters"}
      </Button>

      <Collapse in={showFilters}>
        <Grid container spacing={2} style={{ marginBottom: "20px" }}>
          <Grid item xs={12} sm={4} md={2}>
            <TextField
              select
              label="Supplier"
              value={supplier}
              onChange={handleSupplierChange}
              fullWidth
              size="small"
            >
              {suppliers.map((option) => (
                <MenuItem key={option.name} value={option.name}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <TextField
              select
              label="Shift"
              value={shift}
              onChange={(e) => setShift(e.target.value)}
              fullWidth
              size="small"
              disabled={!supplier}
              SelectProps={{
                multiple: true,
                renderValue: (selected) => (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </div>
                ),
              }}
            >
              {supplierShifts.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <TextField
              select
              label="Form Name"
              value={formName}
              onChange={handleFormNameChange}
              fullWidth
              size="small"
              SelectProps={{
                multiple: true,
                renderValue: (selected) => (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </div>
                ),
              }}
            >
              {/* Select All / Deselect All Option */}
              <MenuItem
                value="all"
                sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5', position: 'sticky', top: 0, zIndex: 1 }}
              >
                <Checkbox
                  checked={formName.length === formNames.length && formNames.length > 0}
                  indeterminate={formName.length > 0 && formName.length < formNames.length}
                />
                <ListItemText
                  primary={
                    formName.length === formNames.length && formNames.length > 0
                      ? 'Deselect All'
                      : 'Select All'
                  }
                />
              </MenuItem>

              {/* Actual Form Options */}
              {formNames.map((option) => (
                <MenuItem key={option} value={option}>
                  <Checkbox checked={formName.includes(option)} />
                  <ListItemText primary={option} />
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={4} md={2}>
            <TextField
              select
              label="Sub Form"
              value={subForm}
              onChange={handleSubFormChange}
              fullWidth
              size="small"
              disabled={availableSubForms.length === 0}
              SelectProps={{
                multiple: true,
                renderValue: (selected) => (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </div>
                ),
              }}
            >
              {/* Select All / Deselect All for SubForm */}
              <MenuItem
                value="all"
                sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5', position: 'sticky', top: 0, zIndex: 1 }}
                disabled={availableSubForms.length === 0}
              >
                <Checkbox
                  checked={subForm.length === availableSubForms.length && availableSubForms.length > 0}
                  indeterminate={subForm.length > 0 && subForm.length < availableSubForms.length}
                />
                <ListItemText
                  primary={
                    subForm.length === availableSubForms.length && availableSubForms.length > 0
                      ? 'Deselect All'
                      : 'Select All'
                  }
                />
              </MenuItem>

              {/* Actual SubForm Options */}
              {availableSubForms.map((option) => (
                <MenuItem key={option} value={option}>
                  <Checkbox checked={subForm.includes(option)} />
                  <ListItemText primary={option} />
                </MenuItem>
              ))}
            </TextField>
          </Grid>        
        </Grid>
      </Collapse>

      {loading && (
        <div style={{ textAlign: "center", margin: "20px 0" }}>
          <CircularProgress />
        </div>
      )}
      {error && (
        <Typography color="error" align="center" style={{ margin: "20px 0" }}>
          {error}
        </Typography>
      )}
      <NonConformanceTable
        data={filteredData}
        selectedForms={formName}
        formMappings={formMappings}
        selectedShifts={shift}
      />
    </div>
  );
};

export default NonConformanceReport;